import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { ClockIcon, DatabaseIcon, DocumentIcon } from "@heroicons/react/outline";

import { VectorStoreModalProps, VectorStore, VectorStoreFile } from "./types";
import { useStoreCreation } from "./hooks/useStoreCreation";
import { useFileUpload } from "./hooks/useFileUpload";
import { useFileManagement } from "./hooks/useFileManagement";

import { FileStoreModal, useStoreSelection } from "@/components/FileStoreModal";
import { NormalizedFile, NormalizedStore } from "@/components/FileStoreModal/types";
import { useVectorStores, useVectorStoreFiles, useDeleteVectorStore } from "@/queries/vector-store";
import { displayErrorToast } from "@/utils/toast";

const getErrorMessage = (err: unknown, defaultMessage: string): string => {
  if (typeof err === "string") {
    return err;
  }
  if (err instanceof Error) {
    return err.message;
  }
  return defaultMessage;
};

export const VectorStoreModal = ({
  open,
  onClose,
  onSelect,
  initialSelectedIds = [],
}: VectorStoreModalProps) => {
  const {
    activeStoreId,
    setActiveStoreId,
    selectedStoreIds,
    toggleStoreSelection,
    isStoreSelected,
  } = useStoreSelection({ initialSelectedIds });

  const {
    isCreating,
    setIsCreating,
    newStoreName,
    setNewStoreName,
    isCreatingStore,
    handleCreateStore,
    handleCancelCreation,
  } = useStoreCreation({
    onStoreCreated: storeId => {
      setActiveStoreId(storeId);
      toggleStoreSelection(storeId);
    },
  });

  const {
    selectedFiles,
    isDragOver,
    uploadingFiles,
    handleFileSelect,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleRemoveFile,
    handleUploadAndAttach,
    isUploading,
  } = useFileUpload({ selectedVectorStoreId: activeStoreId });

  const { handleDeleteFile, isDeletingFileById } = useFileManagement({
    selectedVectorStoreId: activeStoreId,
  });

  const deleteStoreMutation = useDeleteVectorStore();
  const [deletingStoreIds, setDeletingStoreIds] = useState<Set<string>>(new Set());

  const handleDeleteStore = (storeId: string) => {
    if (
      !window.confirm("Are you sure you want to delete this store? This action cannot be undone.")
    ) {
      return;
    }
    setDeletingStoreIds(prev => new Set(prev).add(storeId));
    deleteStoreMutation.mutate(
      { vector_store_id: storeId },
      {
        onSettled: () => {
          setDeletingStoreIds(prev => {
            const next = new Set(prev);
            next.delete(storeId);
            return next;
          });
        },
        onSuccess: () => {
          if (activeStoreId === storeId) {
            setActiveStoreId(null);
          }
        },
      },
    );
  };

  // Data fetching
  const {
    data: vectorStoresData,
    isLoading: isLoadingStores,
    error: vectorStoresError,
  } = useVectorStores(open);
  const {
    data: filesData,
    isLoading: isLoadingFiles,
    error: filesError,
  } = useVectorStoreFiles(activeStoreId || undefined);

  useEffect(() => {
    if (vectorStoresError) {
      displayErrorToast(getErrorMessage(vectorStoresError, "Failed to fetch vector stores"));
    }
  }, [vectorStoresError]);

  useEffect(() => {
    if (filesError) {
      displayErrorToast(getErrorMessage(filesError, "Failed to fetch vector store files"));
    }
  }, [filesError]);

  const vectorStores: VectorStore[] = useMemo(
    () => vectorStoresData?.data || [],
    [vectorStoresData?.data],
  );
  const files: VectorStoreFile[] = useMemo(() => filesData?.data || [], [filesData?.data]);

  // Normalize stores
  const normalizedStores: NormalizedStore[] = useMemo(
    () =>
      vectorStores.map(store => ({
        id: store.id,
        displayName: store.name || "Untitled store",
        fileCount: store.file_counts.total,
      })),
    [vectorStores],
  );

  // Normalize files
  const normalizedFiles: NormalizedFile[] = useMemo(
    () =>
      files.map(file => ({
        id: file.id,
        displayName: file.filename || file.id,
        sizeText: `${Math.round(file.bytes / 1024)} KB`,
        previewId: file.id,
        previewName: file.filename || file.id,
      })),
    [files],
  );

  const selectedStore = vectorStores.find(vs => vs.id === activeStoreId);

  const handleSave = () => {
    if (onSelect) {
      onSelect(selectedStoreIds);
    }
    onClose();
  };

  const renderStoreInfo = (storeId: string) => {
    const store = vectorStores.find(vs => vs.id === storeId);
    if (!store) {
      return null;
    }
    return (
      <>
        <h3 className="text-lg font-semibold">{store.name}</h3>
        <p className="text-sm text-gray-500">ID: {store.id}</p>
        <div className="mt-2 space-y-1 text-sm">
          <div className="flex items-center gap-1 text-gray-600">
            <ClockIcon className="h-4 w-4" />
            <span>Created</span>
            <span className="text-gray-900">
              {moment(store.created_at * 1000).format("MMM D, YYYY, h:mm A")}
            </span>
          </div>
          <div className="flex items-center gap-1 text-gray-600">
            <DatabaseIcon className="h-4 w-4" />
            <span>Storage:</span>
            <span className="text-gray-900">
              {(store.usage_bytes / (1024 * 1024)).toFixed(2)} MB
            </span>
            <span className="text-xs text-gray-500">/ 100 GB limit</span>
          </div>
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <DocumentIcon className="h-3.5 w-3.5" />
            <span>Max file size: 512 MB per file</span>
          </div>
        </div>
      </>
    );
  };

  return (
    <FileStoreModal
      open={open}
      onClose={onClose}
      onSave={handleSave}
      saveDisabled={selectedStoreIds.length === 0}
      title="Manage Vector Stores"
      storeListTitle="Vector Stores"
      emptyStoresMessage="No vector stores found"
      emptyFilesMessage="No files attached"
      selectStoreMessage="Select a vector store to view details"
      filesHeaderLabel={count => `Files Attached (${selectedStore?.file_counts.total ?? count})`}
      isCreating={isCreating}
      setIsCreating={setIsCreating}
      newStoreName={newStoreName}
      setNewStoreName={setNewStoreName}
      isCreatingStore={isCreatingStore}
      handleCreateStore={handleCreateStore}
      handleCancelCreation={handleCancelCreation}
      stores={normalizedStores}
      isLoadingStores={isLoadingStores}
      activeStoreId={activeStoreId}
      setActiveStoreId={setActiveStoreId}
      isStoreSelected={isStoreSelected}
      toggleStoreSelection={toggleStoreSelection}
      onDeleteStore={handleDeleteStore}
      isDeletingStore={storeId => deletingStoreIds.has(storeId)}
      files={normalizedFiles}
      isLoadingFiles={isLoadingFiles}
      uploadingFiles={uploadingFiles}
      selectedFiles={selectedFiles}
      isDragOver={isDragOver}
      isUploading={isUploading}
      handleFileSelect={handleFileSelect}
      handleDragOver={handleDragOver}
      handleDragLeave={handleDragLeave}
      handleDrop={handleDrop}
      handleRemoveFile={handleRemoveFile}
      handleUploadAndAttach={handleUploadAndAttach}
      acceptedFormats=".c,.cpp,.cs,.css,.doc,.docx,.go,.html,.java,.js,.json,.md,.pdf,.php,.pptx,.py,.rb,.sh,.tex,.ts,.txt"
      uploadHelperText="Supported formats: c, cpp, cs, css, doc, docx, go, html, java, jpeg, js, json, md, pdf, php, pptx, py, rb, sh, tar, tex, ts, txt. Max file size: 512 MB per file"
      onDeleteFile={file => handleDeleteFile(file.id)}
      isFileDeleting={isDeletingFileById}
      renderStoreInfo={renderStoreInfo}
    />
  );
};
